import { Box, LinearProgress } from "@mui/material";
import Logo from "assets/images/logo/logo.png";

import React, { createContext, useState, useEffect, useCallback } from "react";
import { auth } from "config/firebaseConfig";
import { useNavigate } from "react-router-dom";
import { useLazyQuery } from "@apollo/client";
import Error from "components/error";
import { GET_USER } from "graphql/users/queries";
import SessionExpiredDialog from "components/Dialogs/SessionExpiredDialog";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [debounce, setDebounce] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [authToken, setAuthToken] = useState(null);

  const [getUserData, { error }] = useLazyQuery(GET_USER, {
    onCompleted: (data) => {
      if (data?.getUser) {
        const extendedUser = {
          ...auth.currentUser,
          ...data.getUser,
        };
        setCurrentUser(extendedUser);
        setIsAdmin(data.getUser.isAdmin || false);
      }
      setLoadingUser(false);
    },
    onError: () => {
      setLoadingUser(false);
    },
  });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const idToken = await user.getIdToken();
        setAuthToken(idToken);
        localStorage.setItem("authToken", idToken);
        setIsAdmin(user.isAdmin || false);
        getUserData();
      } else {
        setCurrentUser(null);
        setIsAdmin(false);
        setLoadingUser(false);
        localStorage.removeItem("authToken");
      }
    });
    return () => unsubscribe();
    // 인증 리스너는 마운트 시 1회만 등록한다. data 를 의존성에 넣으면
    // 쿼리 완료 시마다 리스너가 재구독되어 진행 중이던 getUser 쿼리의
    // concast 가 취소되고, Apollo 내부에서 toQueryResult(undefined) 크래시가 발생한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUserData]);

  useEffect(() => {
    if (error?.graphQLErrors?.[0]?.message?.includes("Not Authenticated")) {
      setIsTokenExpired(true);
      signOut();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error?.graphQLErrors]);

  const signOut = useCallback(() => {
    if (!debounce) {
      setDebounce(true);
      auth.signOut().then(() => {
        localStorage.removeItem("authToken");
        setCurrentUser(null);
        setIsAdmin(false);
        setDebounce(false);
        navigate("/signin");
      });
    }
  }, [debounce, navigate]);

  if (loadingUser) {
    return (
      <>
        <LinearProgress color="orange" />
        <Box
          height="100vh"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Box>
            <img src={Logo} alt="logo" />
          </Box>
        </Box>
      </>
    );
  }
  if (error && !isTokenExpired) {
    console.error("Failed to load user data:", error);
    return (
      <Box height="100vh">
        <Error message="Unexpected Error Occurred... Please try again later..." />
      </Box>
    );
  }

  return (
    <>
      <AuthContext.Provider value={{ currentUser, signOut, isAdmin }}>
        {children}
      </AuthContext.Provider>

      {/* 세션 만료 안내 다이얼로그 */}
      <SessionExpiredDialog
        open={isTokenExpired}
        onRelogin={() => {
          setIsTokenExpired(false);
          navigate("/signin");
        }}
        onClose={() => {
          // 알림만 닫고, 사용자는 이미 로그아웃된 상태
          setIsTokenExpired(false);
        }}
      />
    </>
  );
};
